/*
 * Lazy display-currency conversion.
 *
 * Country detection uses Shopify's same-origin browsing-context endpoint.
 * Exchange rates load only when a non-AED currency is required or the visitor
 * asks for the full list. Checkout remains in the store's configured currency.
 */
(() => {
  const STORAGE_KEY = 'glDisplayCurrency';
  const STORAGE_SOURCE_KEY = 'glDisplayCurrencySource';
  const SESSION_GEO_KEY = 'glCurrencyGeoResolved';
  const RATES_KEY = 'glDisplayCurrencyRatesV3';
  const RATES_TTL = 12 * 60 * 60 * 1000;
  const FETCH_TIMEOUT = 5000;
  const AED_PER_USD = 3.6725;

  const settings = (window.themeVariables && window.themeVariables.settings) || {};
  const BASE = (settings.shopCurrency || 'AED').toUpperCase();
  const MONEY_FORMAT = settings.moneyFormat || '{{amount}}';
  const MONEY_WITH_CURRENCY_FORMAT = settings.moneyWithCurrencyFormat || MONEY_FORMAT;

  // Static ISO mapping avoids a second external geo/country API. Less common
  // and unsupported country currencies intentionally fall back to USD.
  const COUNTRY_CURRENCIES = {
    AED: 'AE', AFN: 'AF', ALL: 'AL', AMD: 'AM', AOA: 'AO', ARS: 'AR', AUD: 'AU,CC,CX,HM,KI,NF,NR,TV',
    AZN: 'AZ', BAM: 'BA', BBD: 'BB', BDT: 'BD', BGN: 'BG', BHD: 'BH', BIF: 'BI', BND: 'BN',
    BOB: 'BO', BRL: 'BR', BSD: 'BS', BTN: 'BT', BWP: 'BW', BYN: 'BY', BZD: 'BZ', CAD: 'CA',
    CDF: 'CD', CHF: 'CH,LI', CLP: 'CL', CNY: 'CN', COP: 'CO', CRC: 'CR', CUP: 'CU', CVE: 'CV',
    CZK: 'CZ', DJF: 'DJ', DKK: 'DK,FO,GL', DOP: 'DO', DZD: 'DZ', EGP: 'EG', ERN: 'ER',
    ETB: 'ET', EUR: 'AD,AT,AX,BE,BL,CY,DE,EE,ES,FI,FR,GF,GP,GR,HR,IE,IT,LT,LU,LV,MC,ME,MF,MQ,MT,NL,PM,PT,RE,SI,SK,SM,TF,VA,YT',
    FJD: 'FJ', GBP: 'GB,GG,GS,IM,JE', GEL: 'GE', GHS: 'GH', GIP: 'GI', GMD: 'GM', GNF: 'GN',
    GTQ: 'GT', GYD: 'GY', HKD: 'HK', HNL: 'HN', HTG: 'HT', HUF: 'HU', IDR: 'ID', ILS: 'IL,PS',
    INR: 'IN', IQD: 'IQ', IRR: 'IR', ISK: 'IS', JMD: 'JM', JOD: 'JO', JPY: 'JP', KES: 'KE',
    KGS: 'KG', KHR: 'KH', KMF: 'KM', KRW: 'KR', KWD: 'KW', KZT: 'KZ', LAK: 'LA', LBP: 'LB',
    LKR: 'LK', LRD: 'LR', LSL: 'LS', LYD: 'LY', MAD: 'MA,EH', MDL: 'MD', MGA: 'MG', MKD: 'MK',
    MMK: 'MM', MNT: 'MN', MOP: 'MO', MRU: 'MR', MUR: 'MU', MVR: 'MV', MWK: 'MW', MXN: 'MX',
    MYR: 'MY', MZN: 'MZ', NAD: 'NA', NGN: 'NG', NIO: 'NI', NOK: 'BV,NO,SJ', NPR: 'NP',
    NZD: 'CK,NU,NZ,PN,TK', OMR: 'OM', PAB: 'PA', PEN: 'PE', PGK: 'PG', PHP: 'PH', PKR: 'PK',
    PLN: 'PL', PYG: 'PY', QAR: 'QA', RON: 'RO', RSD: 'RS', RUB: 'RU', RWF: 'RW', SAR: 'SA',
    SBD: 'SB', SCR: 'SC', SDG: 'SD', SEK: 'SE', SGD: 'SG', SLE: 'SL', SOS: 'SO', SRD: 'SR',
    SSP: 'SS', STN: 'ST', SYP: 'SY', SZL: 'SZ', THB: 'TH', TJS: 'TJ', TMT: 'TM', TND: 'TN',
    TOP: 'TO', TRY: 'TR', TTD: 'TT', TWD: 'TW', TZS: 'TZ', UAH: 'UA', UGX: 'UG',
    USD: 'AS,BQ,EC,FM,GU,IO,MH,MP,PA,PR,PW,SV,TC,TL,UM,US,VG,VI', UYU: 'UY', UZS: 'UZ',
    VES: 'VE', VND: 'VN', VUV: 'VU', WST: 'WS', XAF: 'CF,CG,CM,GA,GQ,TD',
    XCD: 'AG,AI,DM,GD,KN,LC,MS,VC', XOF: 'BF,BJ,CI,GW,ML,NE,SN,TG', XPF: 'NC,PF,WF',
    YER: 'YE', ZAR: 'ZA', ZMW: 'ZM', ZWL: 'ZW',
  };

  function safeRead(storage, key) {
    try { return storage.getItem(key); } catch (_) { return null; }
  }

  function safeWrite(storage, key, value) {
    try { storage.setItem(key, value); } catch (_) {}
  }

  function currencyForCountry(countryCode) {
    const code = String(countryCode || '').toUpperCase();
    for (const [currency, countries] of Object.entries(COUNTRY_CURRENCIES)) {
      if (countries.split(',').includes(code)) return currency;
    }
    return 'USD';
  }

  function currencyName(code) {
    try {
      return new Intl.DisplayNames(undefined, { type: 'currency' }).of(code) || code;
    } catch (_) {
      return code;
    }
  }

  function buildPattern(format) {
    const clean = String(format || '').replace(/<[^>]+>/g, '').trim();
    const match = clean.match(/\{\{\s*\w+\s*\}\}/);
    if (!match) return null;
    const escape = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const before = clean.slice(0, match.index).trim();
    const after = clean.slice(match.index + match[0].length).trim();
    if (!before && !after) return null;
    return new RegExp(`${escape(before)}${before ? '\\s*' : ''}([\\d.,]+)${after ? '\\s*' : ''}${escape(after)}`, 'g');
  }

  const WITH_CURRENCY_PATTERN = buildPattern(MONEY_WITH_CURRENCY_FORMAT);
  const MONEY_PATTERN = buildPattern(MONEY_FORMAT);
  const PRICE_SELECTOR = [
    '[class*="price"]', '[data-price]', '.cart-drawer__button-price',
    '.ecom-predictive-search__item-price', '.ecom-product-single__price--sale',
    '.ecom-product-single__price-normal', '.money', '.transcy-money',
  ].join(',');
  const SKIP_SELECTOR = '.no-currency-convert, currency-switcher';

  const originals = new WeakMap();
  let target = (safeRead(localStorage, STORAGE_KEY) || BASE).toUpperCase();
  let rates = null;
  let ratesPromise = null;
  let observer = null;

  function parseAmount(value) {
    if (/[,]\d{1,2}$/.test(value) && /\./.test(value)) return Number.parseFloat(value.replace(/\./g, '').replace(',', '.'));
    if (/^\d+[,]\d{1,2}$/.test(value)) return Number.parseFloat(value.replace(',', '.'));
    return Number.parseFloat(value.replace(/,/g, ''));
  }

  function formatAmount(amount, code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: code, currencyDisplay: 'code',
        maximumFractionDigits: 0, minimumFractionDigits: 0,
      }).format(amount);
    } catch (_) {
      return `${code} ${Math.round(amount).toLocaleString()}`;
    }
  }

  async function fetchWithTimeout(url) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
    try {
      return await fetch(url, { signal: controller.signal, cache: 'default' });
    } finally {
      clearTimeout(timer);
    }
  }

  async function loadRates() {
    try {
      const cached = JSON.parse(safeRead(localStorage, RATES_KEY) || 'null');
      if (cached?.base === BASE && cached?.rates && Date.now() - cached.fetchedAt < RATES_TTL) return cached.rates;
    } catch (_) {}

    try {
      const response = await fetchWithTimeout(`https://open.er-api.com/v6/latest/${encodeURIComponent(BASE)}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const payload = await response.json();
      if (payload.result !== 'success' || !payload.rates) throw new Error('Invalid rate response');
      safeWrite(localStorage, RATES_KEY, JSON.stringify({ base: BASE, rates: payload.rates, fetchedAt: Date.now() }));
      return payload.rates;
    } catch (_) {
      // Frankfurter is an independent fallback sourced from official reference
      // rates. AED is derived from its fixed USD peg.
      try {
        const response = await fetchWithTimeout('https://api.frankfurter.dev/v1/latest?from=USD');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        if (!payload.rates) throw new Error('Invalid fallback response');
        const converted = { AED: 1, USD: 1 / AED_PER_USD };
        Object.entries(payload.rates).forEach(([code, value]) => { converted[code] = value / AED_PER_USD; });
        safeWrite(localStorage, RATES_KEY, JSON.stringify({ base: BASE, rates: converted, fetchedAt: Date.now() }));
        return converted;
      } catch (_) {
        return null;
      }
    }
  }

  function ensureRates() {
    if (rates) return Promise.resolve(rates);
    if (!ratesPromise) ratesPromise = loadRates().then((value) => { rates = value; return value; });
    return ratesPromise;
  }

  function eachPriceText(root, callback) {
    if (!root?.querySelectorAll) return;
    const containers = root.matches?.(PRICE_SELECTOR) ? [root] : Array.from(root.querySelectorAll(PRICE_SELECTOR));
    containers.forEach((container) => {
      if (container.closest(SKIP_SELECTOR)) return;
      const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
        acceptNode(node) {
          if (!node.nodeValue?.trim() || node.parentElement?.closest(SKIP_SELECTOR)) return NodeFilter.FILTER_REJECT;
          return NodeFilter.FILTER_ACCEPT;
        },
      });
      let node;
      while ((node = walker.nextNode())) callback(node);
    });
  }

  function replacePattern(text, pattern, factor) {
    if (!pattern) return null;
    let matched = false;
    const output = text.replace(new RegExp(pattern.source, 'g'), (full, numeric) => {
      const amount = parseAmount(numeric);
      if (!Number.isFinite(amount)) return full;
      matched = true;
      return formatAmount(amount * factor, target);
    });
    return matched ? output : null;
  }

  function applyConversion(root = document.body) {
    const factor = target === BASE ? 1 : rates?.[target];
    if (!factor) return;

    eachPriceText(root, (node) => {
      if (!originals.has(node)) originals.set(node, node.nodeValue);
      const original = originals.get(node);

      if (target === BASE) {
        node.nodeValue = original;
        return;
      }

      const converted = replacePattern(original, WITH_CURRENCY_PATTERN, factor) || replacePattern(original, MONEY_PATTERN, factor);
      if (converted !== null) node.nodeValue = converted;
    });
  }

  function startObserver() {
    if (observer || target === BASE) return;
    observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) applyConversion(node);
      }));
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function stopObserver() {
    observer?.disconnect();
    observer = null;
  }

  function announceCurrency(code) {
    document.querySelectorAll('[data-currency-toggle-label]').forEach((label) => { label.textContent = code; });
    document.querySelectorAll('[data-currency-toggle]').forEach((button) => {
      button.setAttribute('aria-label', `Change currency. Current currency: ${code}`);
    });
    document.querySelectorAll('[data-currency-code]').forEach((option) => {
      option.setAttribute('aria-selected', option.getAttribute('data-currency-code') === code ? 'true' : 'false');
    });
  }

  async function setCurrency(code, { source = 'user' } = {}) {
    let requested = String(code || 'USD').toUpperCase();
    if (requested !== BASE) {
      const available = await ensureRates();
      if (!available?.[requested]) requested = 'USD';
      if (!available?.[requested]) return;
    }

    target = requested;
    safeWrite(localStorage, STORAGE_KEY, target);
    safeWrite(localStorage, STORAGE_SOURCE_KEY, source);
    announceCurrency(target);
    applyConversion();
    if (target === BASE) stopObserver();
    else startObserver();
  }

  function closePopover(switcher) {
    const popover = document.getElementById(switcher.getAttribute('data-popover-id'));
    if (typeof popover?.hide === 'function') {
      try { popover.hide(); } catch (_) {}
    }
    try { popover?.dispatchEvent(new CustomEvent('dialog:force-close', { bubbles: true })); } catch (_) {}
  }

  function createOption(code) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'popover__value-option h-stack gap-2.5';
    button.setAttribute('role', 'option');
    button.setAttribute('data-currency-code', code);
    button.setAttribute('aria-selected', code === target ? 'true' : 'false');
    const label = document.createElement('span');
    label.textContent = `${currencyName(code)} (${code})`;
    button.appendChild(label);
    return button;
  }

  class CurrencySwitcher extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;
      const list = this.querySelector('[data-currency-options]');
      if (!list) return;

      announceCurrency(target);
      list.addEventListener('click', async (event) => {
        const viewMore = event.target.closest('[data-currency-view-more]');
        if (viewMore) {
          event.preventDefault();
          viewMore.textContent = 'Loading currencies…';
          const available = await ensureRates();
          if (!available) {
            viewMore.textContent = 'Currencies unavailable';
            return;
          }
          const rendered = new Set(Array.from(list.querySelectorAll('[data-currency-code]'), (item) => item.dataset.currencyCode));
          Object.keys(available)
            .filter((code) => /^[A-Z]{3}$/.test(code) && !rendered.has(code))
            .sort((a, b) => currencyName(a).localeCompare(currencyName(b)))
            .forEach((code) => list.insertBefore(createOption(code), viewMore));
          viewMore.remove();
          return;
        }

        const option = event.target.closest('[data-currency-code]');
        if (!option) return;
        event.preventDefault();
        await setCurrency(option.dataset.currencyCode, { source: 'user' });
        closePopover(this);
      });
    }
  }

  function scheduleGeoCurrency() {
    if (safeRead(localStorage, STORAGE_SOURCE_KEY) === 'user') return;
    if (safeRead(sessionStorage, SESSION_GEO_KEY)) return;

    const resolve = async () => {
      safeWrite(sessionStorage, SESSION_GEO_KEY, 'pending');
      let country = '';
      try {
        const root = window.Shopify?.routes?.root || '/';
        const url = new URL(`${root}browsing_context_suggestions.json`, window.location.origin);
        url.searchParams.set('country[enabled]', 'true');
        const response = await fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const payload = await response.json();
        country = payload?.detected_values?.country?.handle || payload?.suggestions?.[0]?.parts?.country?.handle || '';
      } catch (_) {}

      const detectedCurrency = currencyForCountry(country);
      safeWrite(sessionStorage, SESSION_GEO_KEY, detectedCurrency);
      await setCurrency(detectedCurrency, { source: 'geo' });
    };

    const run = () => {
      if ('requestIdleCallback' in window) window.requestIdleCallback(resolve, { timeout: 2500 });
      else window.setTimeout(resolve, 1);
    };
    if (document.readyState === 'complete') run();
    else window.addEventListener('load', run, { once: true });
  }

  if (!customElements.get('currency-switcher')) customElements.define('currency-switcher', CurrencySwitcher);
  announceCurrency(target);
  if (target !== BASE) setCurrency(target, { source: safeRead(localStorage, STORAGE_SOURCE_KEY) || 'geo' });
  scheduleGeoCurrency();
})();
